/**
 * syncQueue.ts — Offline mutation queue for the AMS PWA
 * ─────────────────────────────────────────────────────────────────────────────
 * FLOW:
 *   1. A component calls  queueOrFetch(url, options, tag)
 *   2. If online  → fires the real fetch immediately and returns the result.
 *   3. If offline → saves the request to IndexedDB and returns a stub response
 *                   so the UI can give optimistic feedback ("Saved offline").
 *   4. When the browser detects connectivity restored, the service worker fires
 *      a 'sync' event and calls replayAllQueued() automatically.
 *   5. If Background Sync is not supported (older browsers), we also call
 *      replayAllQueued() whenever the app detects it's back online.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  enqueueSyncItem,
  getAllSyncItems,
  removeSyncItem,
  handleSyncFailure,
  getSyncQueueCount,
} from "./offlineDB";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface QueuedRequest {
  url: string;
  method: string;
  body?: Record<string, unknown> | string | null;
  headers?: Record<string, string>;
  tag?: string;   // e.g. "clock-in" | "leave-apply" | "wfh-request"
}

export interface SyncResult {
  success: boolean;
  synced:  number;
  failed:  number;
}

// ─────────────────────────────────────────────────────────────────────────────
// queueOrFetch — the single entry point for all API mutations
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Use this instead of raw fetch() for any POST/PUT/PATCH/DELETE call.
 *
 * @example
 *   const result = await queueOrFetch(
 *     "http://api/attendance/clock-in",
 *     { method: "POST", body: { employeeId: "E001", time: new Date() } },
 *     "clock-in"
 *   );
 *   if (result.queued) {
 *     showToast("Saved offline — will sync when connected.");
 *   }
 */
export async function queueOrFetch(
  url: string,
  opts: QueuedRequest,
  tag = "mutation"
): Promise<{ queued: boolean; data?: unknown }> {
  const bodyString =
    opts.body == null
      ? null
      : typeof opts.body === "string"
      ? opts.body
      : JSON.stringify(opts.body);

  const defaultHeaders: Record<string, string> = { "Content-Type": "application/json" };
  const headers = { ...defaultHeaders, ...(opts.headers ?? {}) };

  if (navigator.onLine) {
    // ── Online path ──────────────────────────────────────────────────────
    try {
      const res = await fetch(url, {
        method:  opts.method,
        body:    bodyString,
        headers,
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      return { queued: false, data };
    } catch {
      // Network error even though navigator.onLine is true (e.g. server down)
      // → fall through to queueing
    }
  }

  // ── Offline path (or network error) ──────────────────────────────────────
  await enqueueSyncItem({ url, method: opts.method, body: bodyString, headers, tag });

  // Ask the service worker to register a Background Sync tag
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    const registration = await navigator.serviceWorker.ready;
    try {
      await (registration as any).sync.register("ams-sync");
    } catch {
      // Background Sync not supported — replayAllQueued() will be called on
      // the 'online' event in useOffline.ts instead.
    }
  }

  return { queued: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// replayAllQueued — send every pending request in order
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Called:
 *  • By the service worker's 'sync' event (automatic background sync)
 *  • By useOffline.ts when the 'online' event fires (fallback for older browsers)
 *  • Manually via a "Retry Now" button in the UI
 */
export async function replayAllQueued(
  onProgress?: (done: number, total: number) => void
): Promise<SyncResult> {
  const items = await getAllSyncItems();
  let synced = 0;
  let failed = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    onProgress?.(i, items.length);

    try {
      const res = await fetch(item.url, {
        method:  item.method,
        body:    item.body,
        headers: item.headers ?? { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (res.ok || (res.status >= 400 && res.status < 500)) {
        // Success OR client error (4xx — don't retry a bad request)
        await removeSyncItem(item.id!);
        synced++;
      } else {
        // Server error (5xx) — increment retry counter
        await handleSyncFailure(item.id!);
        failed++;
      }
    } catch {
      // Still offline or network error
      await handleSyncFailure(item.id!);
      failed++;
    }
  }

  onProgress?.(items.length, items.length);
  return { success: failed === 0, synced, failed };
}

// ─────────────────────────────────────────────────────────────────────────────
// getPendingCount — how many items are waiting to sync
// ─────────────────────────────────────────────────────────────────────────────

export async function getPendingCount(): Promise<number> {
  return getSyncQueueCount();
}
